import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, SimpleTable, useToast } from '@src/components/ui';
import { downloadSalesReport, fetchSalesSummary } from '../../../api/SalesRepAdminApi';
import { createSalesSummaryColumns } from './tableColumns';
import { mapSalesSummaryRow } from './types';
import styles from './SalesReps.module.scss';
const DEFAULT_FILTERS = { groupBy: 'sales_rep', dateFrom: '', dateTo: '' };
export const SalesSummaryPanel = () => {
    const { showToast } = useToast();
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // Which export is running ('all' or a rep's id); disables the export buttons meanwhile.
    const [exportingKey, setExportingKey] = useState(null);
    const latestRequestIdRef = useRef(0);
    const loadSummary = useCallback(async (active) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            setLoading(true);
            const response = await fetchSalesSummary({
                groupBy: active.groupBy,
                dateFrom: active.dateFrom || undefined,
                dateTo: active.dateTo || undefined,
            });
            if (requestId !== latestRequestIdRef.current)
                return;
            const results = response.body?.results ?? [];
            setRows(results.map(mapSalesSummaryRow));
        }
        catch (error) {
            if (requestId !== latestRequestIdRef.current)
                return;
            console.error('Error loading sales summary:', error);
            const message = error?.response?.body?.dateFrom?.[0] ?? 'Failed to load sales summary';
            showToast({ message, variant: 'error', noBorder: true });
            setRows([]);
        }
        finally {
            if (requestId === latestRequestIdRef.current)
                setLoading(false);
        }
    }, [showToast]);
    useEffect(() => {
        loadSummary(appliedFilters);
    }, [loadSummary, appliedFilters]);
    const handleReset = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
    };
    // Exports use the filters currently applied to the table, not unsaved edits in the form.
    const runExport = async (key, salesRepId) => {
        if (exportingKey)
            return;
        setExportingKey(key);
        try {
            await downloadSalesReport({
                salesRepId,
                dateFrom: appliedFilters.dateFrom || undefined,
                dateTo: appliedFilters.dateTo || undefined,
            });
        }
        catch (error) {
            console.error('Error exporting sales report:', error);
            showToast({
                message: error?.message || 'Failed to download sales report',
                variant: 'error',
                noBorder: true,
            });
        }
        finally {
            setExportingKey(null);
        }
    };
    const invalidRange = !!filters.dateFrom && !!filters.dateTo && filters.dateFrom > filters.dateTo;
    return (_jsxs("section", { className: styles.summarySection, "aria-label": "Sales summary", children: [_jsx("h2", { className: styles.summaryTitle, children: "Sales Summary" }), _jsx("p", { className: styles.summaryHint, children: "Sales attributed to the rep or territory recorded on each order when it was placed. Excludes cancelled and failed-payment orders. Net sales = gross sales \u2212 discounts (before shipping and tax)." }), _jsxs("div", { className: styles.filterBar, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-summary-group-by", children: "Group By" }), _jsxs("select", { id: "sales-summary-group-by", value: filters.groupBy, onChange: (event) => setFilters((prev) => ({ ...prev, groupBy: event.target.value })), children: [_jsx("option", { value: "sales_rep", children: "Sales Rep" }), _jsx("option", { value: "territory", children: "Territory" })] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-summary-date-from", children: "From" }), _jsx("input", { id: "sales-summary-date-from", type: "date", value: filters.dateFrom, onChange: (event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value })) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-summary-date-to", children: "To" }), _jsx("input", { id: "sales-summary-date-to", type: "date", value: filters.dateTo, onChange: (event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value })) })] }), _jsxs("div", { className: styles.filterActions, children: [_jsx(Button, { variant: "primaryV2", size: "small", label: "Apply", onClick: () => setAppliedFilters(filters), disabled: invalidRange }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Reset", onClick: handleReset }), _jsx(Button, { variant: "secondaryV2", size: "small", label: exportingKey === 'all' ? 'Exporting...' : 'Export All Reps', onClick: () => runExport('all'), disabled: !!exportingKey })] })] }), invalidRange && (_jsx("div", { className: styles.inlineErrorBanner, role: "alert", children: "The start date must not be after the end date." })), loading && _jsx("div", { children: "Loading..." }), !loading && rows.length === 0 ? (_jsx("div", { className: styles.emptyState, children: "No sales found for the selected filters." })) : (_jsx(SimpleTable, { columns: createSalesSummaryColumns(appliedFilters.groupBy, {
                    onExport: (row) => runExport(row.rep_uuid, row.rep_uuid),
                    exportDisabled: !!exportingKey,
                }), data: rows, rowHeight: "52px", hideTopBorder: true }))] }));
};
export default SalesSummaryPanel;
